import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Interface pour l'état de navigation
 * Permet de garder trace de la page d'origine pour le retour
 */
export interface NavigationState {
  from?: string;
  fromLabel?: string;
  accountCode?: string;
  accountId?: string;
}

/**
 * Hook pour la navigation avec retour
 * Permet de naviguer vers une page et revenir à la page d'origine
 */
export function useNavigationWithReturn() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Naviguer vers une page avec l'état de retour
   */
  const navigateTo = useCallback((
    path: string,
    state?: Omit<NavigationState, 'from'>
  ) => {
    navigate(path, {
      state: {
        ...state,
        from: location.pathname,
        fromLabel: getPageLabel(location.pathname)
      }
    });
  }, [navigate, location.pathname]);

  /**
   * Naviguer vers le grand livre avec un compte sélectionné
   */
  const navigateToLedger = useCallback((
    accountCode?: string,
    accountId?: string
  ) => {
    const searchParams = new URLSearchParams();
    if (accountCode) searchParams.set('account', accountCode);
    if (accountId) searchParams.set('accountId', accountId);
    
    const path = `/ledger${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    navigate(path, {
      state: {
        from: location.pathname,
        fromLabel: getPageLabel(location.pathname),
        accountCode,
        accountId
      }
    });
  }, [navigate, location.pathname]);

  /**
   * Revenir à la page précédente (si état disponible) ou au dashboard
   */
  const goBack = useCallback(() => {
    const state = location.state as NavigationState | undefined;
    if (state?.from) {
      navigate(state.from);
    } else {
      navigate(-1);
    }
  }, [navigate, location.state]);

  /**
   * Obtenir l'état de navigation actuel
   */
  const getNavigationState = useCallback((): NavigationState | null => {
    return location.state as NavigationState | null;
  }, [location.state]);

  /**
   * Vérifier si on vient d'une autre page (pour afficher le bouton retour)
   */
  const hasReturnPath = useCallback((): boolean => {
    const state = location.state as NavigationState | undefined;
    return !!state?.from;
  }, [location.state]);

  return {
    navigateTo,
    navigateToLedger,
    goBack,
    getNavigationState,
    hasReturnPath,
    currentPath: location.pathname
  };
}

/**
 * Obtenir le libellé d'une page à partir de son path
 */
function getPageLabel(path: string): string {
  const labels: Record<string, string> = {
    '/': 'Tableau de bord',
    '/dashboard': 'Tableau de bord',
    '/ledger': 'Grand livre',
    '/journals': 'Journaux',
    '/reports': 'Rapports',
    '/declarations': 'Déclarations',
    '/settings': 'Paramètres',
    '/fiscal-years': 'Exercices fiscaux',
    '/users': 'Utilisateurs',
    '/organization': 'Organisation',
    '/chat': 'Assistant IA'
  };
  
  return labels[path] || 'Page précédente';
}
